/**
 * ONBOARDING. UF_01.
 *
 * The tier ladder, walked in one sitting. The order is not negotiable and each step is a
 * prerequisite of the next:
 *
 *   register  ->  SET A PIN  ->  consent and disclosure  ->  identity (T1)  ->  funds (T2)
 *
 * ## Why the PIN is second
 *
 * Every step after registration needs a SESSION, and a session needs a PIN. Consent in particular is
 * recorded against a participant the backend has to be certain of: a consent record anybody could
 * write against anybody's id is not evidence of anything, and it is the document we would hand a
 * regulator asking whether this person agreed.
 *
 * ## Why the disclosure gate is real
 *
 * It is enforced by the BACKEND, not by this page. Skip it here and `verifyTier1` fails with
 * DISCLOSURE_NOT_ACKNOWLEDGED (FR-X-DSP-004). A participant who has not been told they are in an
 * experimental sandbox does not get to hold value in it, and that rule does not depend on a
 * checkbox in a browser.
 *
 * The identity and funds forms themselves live in `components/kyc-forms`, shared with the tier-upgrade
 * routes. Written twice, they would drift, and the KYC file is the thing a regulator reads.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { api, LipaApiError } from '@/lib/api';
import { setToken } from '@/lib/session';
import { useSession } from '@/lib/session-context';
import { Tier1Form, Tier2Form, UnderReview } from '@/components/kyc-forms';
import { Brandmark } from '@/components/brand';
import { ThemeToggle } from '@/components/theme-toggle';
import { OtpField, PinField } from '@/components/auth-fields';

type Step = 'register' | 'pin' | 'verify' | 'consent' | 'identity' | 'funds' | 'review' | 'done';

const STEPS = [
  { key: 'register', label: 'Account' },
  { key: 'pin', label: 'PIN' },
  { key: 'verify', label: 'Contact' },
  { key: 'consent', label: 'Consent' },
  { key: 'identity', label: 'Identity' },
  { key: 'funds', label: 'Funds' },
] as const;

const ORDER: Step[] = [
  'register',
  'pin',
  'verify',
  'consent',
  'identity',
  'funds',
  'review',
  'done',
];

export default function Onboarding() {
  const navigate = useNavigate();
  const { refresh } = useSession();

  const [step, setStep] = useState<Step>('register');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [phone, setPhone] = useState('+2547');
  const [email, setEmail] = useState('');
  const [id, setId] = useState<string | null>(null);

  // The ONE-TIME token from registration. It authorizes the first PIN and nothing else, it is good
  // for a single use, and it is never persisted: reload before setting a PIN and you start again.
  // That is the right trade. The alternative is leaving an account-enrolment credential lying around
  // in browser storage.
  const [enrolmentToken, setEnrolmentToken] = useState<string | null>(null);

  const [pin, setPin] = useState('');
  const [pinAgain, setPinAgain] = useState('');

  const [dataConsent, setDataConsent] = useState(false);
  const [riskAck, setRiskAck] = useState(false);

  const fail = (e: unknown) =>
    setError(
      e instanceof LipaApiError ? e.detail.message : 'Something went wrong. Please try again.',
    );

  async function register(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);

    try {
      const p = await api.register(phone, email, 'individual');
      setId(p.id);
      setEnrolmentToken(p.enrolment_token);
      setStep('pin');
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }

  async function enrol(e: React.FormEvent) {
    e.preventDefault();
    if (!enrolmentToken) return;

    if (pin !== pinAgain) {
      setError('Those two PINs are not the same.');
      return;
    }

    setBusy(true);
    setError(null);

    try {
      // Sets the PIN and signs in, in one step. Setting a PIN and then immediately asking the user to
      // type it back into a sign-in form would teach them that being asked for their PIN twice in a
      // row is normal, and that is exactly the habit a phishing page needs them to have.
      const session = await api.enrolPin(enrolmentToken, pin);
      setToken(session.token);
      setEnrolmentToken(null); // consumed; worthless now, and it should not linger
      setPin('');
      setPinAgain('');
      await refresh();
      // Contact verification (FR-U-ID-002) comes next. It needs a session, which we now have, and
      // it must happen before Tier 1: the backend gate refuses tier1 until both channels are proven.
      setStep('verify');
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }

  async function consent() {
    if (!id) return;
    setBusy(true);
    setError(null);

    try {
      // TWO records, not one. They are different legal instruments, recorded in different immutable
      // tables: the data-processing basis under the Data Protection Act, and proof the participant was
      // told what the sandbox is. Bundling them into a single "I agree" would make it impossible to
      // demonstrate that either was given specifically, which is the entire point of recording them.
      await api.consent(id, 'data_processing');
      await api.consent(id, 'sandbox_risk');
      setStep('identity');
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }

  async function finish() {
    await refresh();
    setStep('done');
    setTimeout(() => navigate('/home'), 900);
  }

  return (
    <main className="mx-auto max-w-md px-4 py-8">
      <div className="mb-2 flex justify-end">
        <ThemeToggle />
      </div>
      <header className="mb-8 flex flex-col items-center text-center">
        <Brandmark size="lg" />
        <p className="mt-3 text-sm text-ink-muted">
          Virtual assets, fiat, and credit. Compliance first.
        </p>
      </header>

      {/* The ladder, always visible, so the participant can see where they are and what is next. */}
      <ol className="mb-6 flex items-center justify-center gap-1.5 text-xs text-ink-muted">
        {STEPS.map((s, i) => {
          const done = ORDER.indexOf(step) > i;
          const active = step === s.key;

          return (
            <li key={s.key} className="flex items-center gap-1.5">
              <span
                className={`flex h-6 w-6 items-center justify-center rounded-full text-micro font-medium ${
                  done
                    ? 'bg-accent text-white'
                    : active
                      ? 'bg-primary text-white'
                      : 'bg-border text-ink-muted'
                }`}
              >
                {done ? '✓' : i + 1}
              </span>
              <span className={active ? 'font-medium text-ink' : ''}>{s.label}</span>
              {i < STEPS.length - 1 ? <span className="mx-0.5 h-px w-2 bg-border" /> : null}
            </li>
          );
        })}
      </ol>

      <div className="rounded-xl border border-border bg-surface p-6 shadow-card">
        {error ? (
          <div
            role="alert"
            className="mb-4 rounded-field border border-danger/30 bg-danger/5 p-3 text-sm text-danger"
          >
            {error}
          </div>
        ) : null}

        {step === 'register' ? (
          <form onSubmit={register} className="space-y-4">
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-ink">
                Phone number
              </label>
              <input
                id="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+254712345678"
                required
                className="mt-1 w-full rounded-field border border-border bg-surface px-3 py-2.5 font-mono text-base text-ink outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/25"
              />
              <p className="mt-1 text-xs text-ink-muted">
                A Kenyan mobile number. The sandbox operates in Kenya.
              </p>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-ink">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.co.ke"
                required
                className="mt-1 w-full rounded-field border border-border bg-surface px-3 py-2.5 text-base text-ink outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/25"
              />
            </div>

            <button
              type="submit"
              disabled={busy}
              className="w-full rounded-field bg-primary py-3 text-sm font-semibold text-white shadow-soft transition hover:bg-primary-light disabled:opacity-50"
            >
              {busy ? 'Creating your account…' : 'Create account'}
            </button>

            <p className="text-center text-xs text-ink-muted">
              Your account starts unverified. You will not be able to hold or move value until you
              complete verification.
            </p>

            <p className="text-center text-sm text-ink-muted">
              Already have an account?{' '}
              <Link to="/signin" className="font-medium text-brand underline underline-offset-2">
                Sign in
              </Link>
            </p>
          </form>
        ) : null}

        {step === 'pin' ? (
          <form onSubmit={enrol} className="space-y-4">
            <h2 className="font-semibold text-ink">Choose a PIN</h2>
            <p className="text-sm text-ink-muted">
              This is how you will sign in, and how you will authorise a payment. It is the only
              thing standing between somebody holding your phone and holding your money.
            </p>

            <div>
              <p className="mb-2 text-sm font-medium text-ink">New PIN</p>
              <PinField value={pin} onChange={setPin} length={4} autoFocus confirm />
              <p className="mt-2 text-xs text-ink-muted">
                Four digits. Not 1234, not 0000, and not all the same digit: those are the first
                ones anybody guesses.
              </p>
            </div>

            <div>
              <p className="mb-2 text-sm font-medium text-ink">Confirm PIN</p>
              <PinField
                value={pinAgain}
                onChange={setPinAgain}
                length={4}
                confirm
                invalid={!!error}
              />
            </div>

            <button
              type="submit"
              disabled={busy || pin.length < 4}
              className="w-full rounded-field bg-primary py-3 text-sm font-semibold text-white shadow-soft transition hover:bg-primary-light disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy ? 'Setting your PIN...' : 'Set PIN and continue'}
            </button>

            <p className="text-center text-xs text-ink-muted">
              Five wrong attempts locks the account for fifteen minutes. That is deliberate: it is
              what makes a short PIN safe to use at all.
            </p>
          </form>
        ) : null}

        {step === 'verify' && id ? (
          <ContactVerifyStep participantId={id} onDone={() => setStep('consent')} />
        ) : null}

        {step === 'consent' ? (
          <div className="space-y-4">
            <h2 className="font-semibold text-ink">Before you continue</h2>

            <div className="rounded-lg bg-canvas p-4 text-sm leading-relaxed text-ink-muted">
              <p>
                VASP operates inside the{' '}
                <strong className="font-medium text-ink">
                  Capital Markets Authority regulatory sandbox
                </strong>
                . This is a supervised, experimental environment.
              </p>
              <ul className="mt-3 list-disc space-y-1 pl-4">
                <li>
                  Transaction values are capped, and access is limited to verified participants.
                </li>
                <li>Every value movement is held in escrow while it settles.</li>
                <li>The protections available to you differ from a fully licensed service.</li>
                <li>The regulator has live, read-only visibility of activity on the platform.</li>
              </ul>
            </div>

            <label className="flex cursor-pointer items-start gap-3 text-sm">
              <input
                type="checkbox"
                checked={dataConsent}
                onChange={(e) => setDataConsent(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-border text-brand focus:ring-primary"
              />
              <span className="text-ink">
                I consent to VASP processing my personal data, as described in the privacy
                notice, under the Data Protection Act 2019.
              </span>
            </label>

            <label className="flex cursor-pointer items-start gap-3 text-sm">
              <input
                type="checkbox"
                checked={riskAck}
                onChange={(e) => setRiskAck(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-border text-brand focus:ring-primary"
              />
              <span className="text-ink">
                I understand this is a regulatory sandbox, and I acknowledge the risk disclosure.
              </span>
            </label>

            <button
              onClick={consent}
              disabled={busy || !dataConsent || !riskAck}
              className="w-full rounded-field bg-primary py-3 text-sm font-semibold text-white shadow-soft transition hover:bg-primary-light disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy ? 'Recording…' : 'Continue'}
            </button>

            <p className="text-center text-xs text-ink-muted">
              Your acknowledgement is time-stamped and recorded immutably.
            </p>
          </div>
        ) : null}

        {step === 'identity' && id ? (
          <div className="space-y-4">
            <h2 className="font-semibold text-ink">Verify your identity</h2>
            <p className="text-sm text-ink-muted">
              We check the name on your ID against sanctions, PEP, and watchlists. This is what
              makes VASP a whitelisted environment.
            </p>

            <Tier1Form
              participantId={id}
              onDone={async (outcome) => {
                if (outcome === 'under_review') {
                  setStep('review');
                  return;
                }
                await refresh();
                // T1 can HOLD value. It cannot MOVE it. Carrying straight on beats dropping the user
                // at a home screen whose main button is disabled for a reason nobody explained.
                setStep('funds');
              }}
            />
          </div>
        ) : null}

        {step === 'funds' && id ? (
          <div className="space-y-4">
            <h2 className="font-semibold text-ink">Where your money comes from</h2>
            <p className="text-sm text-ink-muted">
              Your identity is verified and you can hold value. To <strong>send</strong> it, we need
              your address and a declaration of where your funds come from.
            </p>

            <Tier2Form
              participantId={id}
              onDone={async (outcome) => {
                if (outcome === 'under_review') {
                  setStep('review');
                  return;
                }
                await finish();
              }}
            />
          </div>
        ) : null}

        {step === 'review' ? <UnderReview /> : null}

        {step === 'done' ? (
          <div className="py-6 text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-accent/10">
              <svg
                className="h-6 w-6 text-accent"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h2 className="font-semibold text-ink">You are verified</h2>
            <p className="mt-1 text-sm text-ink-muted">
              Screened, whitelisted, and your wallet is open.
            </p>
          </div>
        ) : null}
      </div>
    </main>
  );
}

/**
 * CONTACT VERIFICATION (FR-U-ID-002). Prove the phone and the email by one-time code, before Tier 1.
 *
 * Both channels must be proven. The backend gate refuses tier1 until they are, which is why this
 * sits in the onboarding ladder and not in a settings screen nobody visits. Under the simulator the
 * code comes back in the request response (`dev_code`) so a sandbox tester can complete the flow;
 * with a real SMS or email gateway it arrives out of band and the hint is absent.
 */
function ContactVerifyStep({
  participantId,
  onDone,
}: {
  participantId: string;
  onDone: () => void;
}) {
  const channels = ['phone', 'email'] as const;
  type Channel = (typeof channels)[number];

  const [code, setCode] = useState<Record<Channel, string>>({ phone: '', email: '' });
  const [hint, setHint] = useState<Record<Channel, string | null>>({ phone: null, email: null });
  const [verified, setVerified] = useState<Record<Channel, boolean>>({
    phone: false,
    email: false,
  });
  const [busy, setBusy] = useState<Channel | null>(null);
  const [error, setError] = useState<string | null>(null);

  const request = useCallback(
    async (channel: Channel) => {
      setError(null);
      try {
        const r = await api.requestContactCode(participantId, channel);
        // Under the simulator the code is handed back so onboarding is completable without logs.
        setHint((h) => ({ ...h, [channel]: r.dev_code }));
        if (r.dev_code) setCode((c) => ({ ...c, [channel]: r.dev_code as string }));
      } catch (e) {
        setError(e instanceof LipaApiError ? e.message : 'We could not send a code.');
      }
    },
    [participantId],
  );

  // Send both codes as the step opens. A tester should not have to hunt for a Send button.
  useEffect(() => {
    void request('phone');
    void request('email');
  }, [request]);

  async function verify(channel: Channel) {
    setBusy(channel);
    setError(null);
    try {
      const status = await api.verifyContactCode(participantId, channel, code[channel]);
      setVerified({ phone: status.phone_verified, email: status.email_verified });
    } catch (e) {
      setError(e instanceof LipaApiError ? e.message : 'That code was not accepted.');
    } finally {
      setBusy(null);
    }
  }

  const both = verified.phone && verified.email;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="font-semibold text-ink">Confirm your phone and email</h2>
        <p className="mt-1 text-sm text-ink-muted">
          We sent a six digit code to each. Enter both to continue. This proves the contact details
          are really yours before your account is verified.
        </p>
      </div>

      {channels.map((channel) => (
        <div key={channel} className="rounded-lg border border-border p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium capitalize text-ink">{channel}</span>
            {verified[channel] ? (
              <span className="text-xs font-medium text-accent">Verified</span>
            ) : null}
          </div>

          {!verified[channel] ? (
            <div className="mt-3 space-y-3">
              <OtpField
                value={code[channel]}
                onChange={(v) => setCode((c) => ({ ...c, [channel]: v }))}
                invalid={!!error}
                ariaLabel={`${channel} code`}
              />
              <button
                onClick={() => verify(channel)}
                disabled={busy === channel || code[channel].length < 6}
                className="w-full rounded-field bg-primary py-2 text-sm font-medium text-white transition hover:bg-primary-light disabled:opacity-50"
              >
                {busy === channel ? 'Verifying...' : 'Verify'}
              </button>
            </div>
          ) : null}

          <div className="mt-1.5 flex items-center justify-between text-xs text-ink-muted">
            {hint[channel] ? (
              <span className="font-mono text-info">Simulator code: {hint[channel]}</span>
            ) : (
              <span>Did not arrive?</span>
            )}
            {!verified[channel] ? (
              <button onClick={() => request(channel)} className="underline hover:text-ink">
                Resend
              </button>
            ) : null}
          </div>
        </div>
      ))}

      {error ? (
        <div
          role="alert"
          className="rounded-lg border border-danger/30 bg-danger/5 p-3 text-sm text-danger"
        >
          {error}
        </div>
      ) : null}

      <button
        onClick={onDone}
        disabled={!both}
        className="w-full rounded-field bg-primary py-3 text-sm font-semibold text-white shadow-soft transition hover:bg-primary-light disabled:opacity-50"
      >
        Continue
      </button>
    </div>
  );
}
